/**
 * Shared card background + pattern styles for admin preview and storefront widget.
 * Mirror: extension/.../assets/bar-design-shared.js (keep PATTERN_LAYERS in sync).
 */
export const PATTERN_LAYERS = {
  sparkle: {
    backgroundImage:
      "radial-gradient(circle, rgba(255, 255, 255, 0.9) 1px, transparent 1.5px), radial-gradient(circle, rgba(255, 255, 255, 0.55) 1px, transparent 1.5px)",
    backgroundSize: "18px 18px, 28px 28px",
    backgroundPosition: "0 0, 12px 8px",
    opacity: 0.85,
  },
  hearts: {
    backgroundImage: "radial-gradient(circle at 50% 35%, rgba(244, 114, 182, 0.35) 0 28%, transparent 30%)",
    backgroundSize: "36px 36px",
    opacity: 0.65,
  },
  spiderweb: {
    backgroundImage:
      "repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.08) 0 1px, transparent 1px 14px), repeating-linear-gradient(-45deg, rgba(255, 255, 255, 0.08) 0 1px, transparent 1px 14px), radial-gradient(circle at 0 0, rgba(255, 255, 255, 0.12) 0, transparent 45%), radial-gradient(circle at 100% 0, rgba(255, 255, 255, 0.12) 0, transparent 45%)",
    opacity: 0.9,
  },
  christmas: {
    backgroundImage:
      "radial-gradient(circle at 12% 80%, rgba(34, 197, 94, 0.35) 0 8px, transparent 9px), radial-gradient(circle at 88% 75%, rgba(234, 179, 8, 0.45) 0 10px, transparent 11px), radial-gradient(circle at 70% 20%, rgba(234, 179, 8, 0.3) 0 6px, transparent 7px), linear-gradient(135deg, transparent 40%, rgba(180, 83, 9, 0.15) 40% 42%, transparent 42%)",
    backgroundSize: "100% 100%",
    opacity: 0.85,
  },
  snow: {
    backgroundImage:
      "radial-gradient(ellipse 120% 55% at 50% 115%, #ffffff 0 58%, transparent 60%), radial-gradient(ellipse 90% 45% at 15% 108%, #ffffff 0 52%, transparent 54%), radial-gradient(ellipse 90% 45% at 85% 108%, #ffffff 0 52%, transparent 54%)",
    opacity: 0.95,
  },
  lights: {
    backgroundImage:
      "radial-gradient(circle, #60a5fa 2px, transparent 2.5px), radial-gradient(circle, #fbbf24 2px, transparent 2.5px), radial-gradient(circle, #f87171 2px, transparent 2.5px), radial-gradient(circle, #ffffff 2px, transparent 2.5px)",
    backgroundSize: "48px 28px, 56px 32px, 52px 30px, 60px 34px",
    backgroundPosition: "0 8px, 18px 4px, 36px 10px, 8px 14px",
    opacity: 0.75,
  },
};

function num(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

export function resolveCardBackground(card) {
  if (!card) return "#FFFFFF";
  if (card.background_type === "image" && card.background_image) {
    return `url(${card.background_image}) center/cover no-repeat`;
  }
  if (card.background_type === "gradient" && card.gradient) {
    const g = card.gradient;
    return `linear-gradient(${num(g.angle, 90)}deg, ${g.from || "#FFFFFF"}, ${g.to || "#F6F6F7"})`;
  }
  return card.background || "#FFFFFF";
}

export function shouldShowPattern(card) {
  if (!card || !card.background_pattern || !PATTERN_LAYERS[card.background_pattern]) return false;
  if (card.background_type === "gradient" || card.background_type === "image") return false;
  return true;
}

export function patternOverlayStyle(card) {
  if (!shouldShowPattern(card)) return null;
  return {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 0,
    ...PATTERN_LAYERS[card.background_pattern],
  };
}

export function cardContainerStyle(card, placementType, layout) {
  const sp = card?.spacing || {};
  const style = {
    border: `${num(card?.border_size, 1)}px solid ${card?.border_color || "#E3E3E3"}`,
    borderRadius: placementType === "announcement_bar" ? 0 : num(card?.corner_radius, 8),
    padding: `${num(sp.inside_top, 16)}px 16px ${num(sp.inside_bottom, 16)}px`,
    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
  };
  if (style.border.startsWith("0px") && (card?.border_color === "transparent" || !card?.border_color)) {
    style.border = "0";
  }
  if (card?.background_type === "gradient" && card?.gradient) {
    style.background = resolveCardBackground(card);
  } else if (card?.background_type === "image" && card?.background_image) {
    style.background = resolveCardBackground(card);
  } else {
    style.backgroundColor = card?.background || "#FFFFFF";
    if (shouldShowPattern(card)) {
      const layer = PATTERN_LAYERS[card.background_pattern];
      style.backgroundImage = layer.backgroundImage;
      if (layer.backgroundSize) style.backgroundSize = layer.backgroundSize;
      if (layer.backgroundPosition) style.backgroundPosition = layer.backgroundPosition;
      style.backgroundRepeat = "repeat";
    } else {
      style.backgroundImage = "none";
    }
  }
  if (style.border.startsWith("0px") && (card?.border_color === "transparent" || !card?.border_color)) {
    style.border = "0";
  }
  const widthPct = num(layout?.widget_width_percent, 100);
  if (placementType === "home_page") {
    if (widthPct >= 100) {
      style.width = "100%";
      style.maxWidth = "100%";
    } else {
      style.width = `${widthPct}%`;
      style.marginLeft = "auto";
      style.marginRight = "auto";
    }
  } else if (placementType === "cart_page" || placementType === "product_page") {
    style.width = `${widthPct}%`;
    style.maxWidth = "100%";
    if (placementType === "cart_page" && widthPct < 100) {
      style.marginLeft = "auto";
      style.marginRight = "auto";
    }
  } else if (placementType === "announcement_bar") {
    style.width = "100%";
  }
  return style;
}

export function cardOuterStyle(card, placementType, layout) {
  const sp = card?.spacing || {};
  const widthPct = num(layout?.widget_width_percent, 100);
  const flushHome = placementType === "home_page" && widthPct >= 100;
  const defaultTop = placementType === "home_page" ? 0 : 12;
  const defaultBottom = placementType === "home_page" ? 0 : 12;
  const top = flushHome ? 0 : num(sp.outside_top, defaultTop);
  const bottom = flushHome ? 0 : num(sp.outside_bottom, defaultBottom);
  return {
    margin: `${top}px 0 ${bottom}px`,
  };
}

/** Vertical space around the shipping message (product page gets extra breathing room). */
export function messageMargins(placementType, hasContentBelow = true) {
  const isProduct = placementType === "product_page";
  return {
    marginTop: isProduct ? 8 : 0,
    marginBottom: hasContentBelow ? (isProduct ? 16 : 10) : isProduct ? 8 : 0,
  };
}

export function messageStyle(typography, placementType, hasContentBelow = true) {
  const msgTypo = typography?.message || {};
  const { marginTop, marginBottom } = messageMargins(placementType, hasContentBelow);
  return {
    position: "relative",
    zIndex: 1,
    fontSize: num(msgTypo.size, 16),
    color: msgTypo.color || "#202223",
    marginTop,
    marginBottom,
    marginLeft: 0,
    marginRight: 0,
    textAlign: "center",
    fontWeight: 600,
    lineHeight: 1.35,
  };
}

/** Serialize pattern overlay styles for storefront HTML (inline, theme-safe). */
export function patternOverlayInlineCss(card) {
  const layer = shouldShowPattern(card) ? PATTERN_LAYERS[card.background_pattern] : null;
  if (!layer) return "";
  return Object.entries(layer)
    .map(([key, value]) => {
      const cssKey = key.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase());
      return `${cssKey}:${value}`;
    })
    .join(";");
}

export function patternOverlayHtml(card) {
  if (!shouldShowPattern(card)) return "";
  return `<div class="fsb-pattern" style="position:absolute;inset:0;pointer-events:none;z-index:0;${patternOverlayInlineCss(card)}" aria-hidden="true"></div>`;
}
